import React, { useState, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import { LayoutTemplate, Plus, Send, Trash2, FileText, Image as ImageIcon, File, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Layout {
  id: string;
  type: string;
  title: string;
  content: string | null;
  file_path: string | null;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
}

export type LayoutsPopoverHandle = { open: () => void };

interface LayoutsPopoverProps {
  workspaceId: string;
  onInsertText: (text: string) => void;
  onSendFile: (file: File) => void;
  disabled?: boolean;
  /** false = gatilho invisível (abrir via ref no menu ⋮ mobile) */
  showTrigger?: boolean;
}

type LayoutType = "text" | "image" | "file";

const MAX_LAYOUTS_BY_TYPE: Record<LayoutType, number> = {
  text: 20,
  image: 5,
  file: 5,
};

const getMaxForType = (type: LayoutType) => MAX_LAYOUTS_BY_TYPE[type];

const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const LayoutsPopover = forwardRef<LayoutsPopoverHandle, LayoutsPopoverProps>(function LayoutsPopover(
  { workspaceId, onInsertText, onSendFile, disabled, showTrigger = true },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<"text" | "image" | "file">("text");
  const [createTitle, setCreateTitle] = useState("");
  const [createContent, setCreateContent] = useState("");
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const fetchLayouts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("workspace_layouts")
      .select("id, type, title, content, file_path, file_name, file_size, mime_type")
      .eq("workspace_id", workspaceId)
      .order("type")
      .order("created_at", { ascending: true });
    if (!error && data) {
      setLayouts(data);
    }
    setLoading(false);
    setLoaded(true);
  }, [workspaceId]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen && !loaded) {
        void fetchLayouts();
      }
    },
    [loaded, fetchLayouts],
  );

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        if (disabled) return;
        handleOpenChange(true);
      },
    }),
    [disabled, handleOpenChange],
  );

  const countByType = (type: string) => layouts.filter(l => l.type === type).length;

  const openCreateDialog = (type: LayoutType) => {
    const maxForType = getMaxForType(type);
    if (countByType(type) >= maxForType) {
      toast({ title: "Limite atingido", description: `Máximo de ${maxForType} layouts do tipo "${type}" já cadastrados.`, variant: "destructive" });
      return;
    }
    setCreateType(type);
    setCreateTitle("");
    setCreateContent("");
    setCreateFile(null);
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!createTitle.trim()) {
      toast({ title: "Título obrigatório", variant: "destructive" });
      return;
    }
    if (createType === "text" && !createContent.trim()) {
      toast({ title: "Conteúdo obrigatório", variant: "destructive" });
      return;
    }
    if ((createType === "image" || createType === "file") && !createFile) {
      toast({ title: "Arquivo obrigatório", variant: "destructive" });
      return;
    }
    if (createFile && createFile.size > MAX_FILE_SIZE) {
      toast({ title: "Arquivo muito grande", description: "Máximo 16MB.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      let filePath: string | null = null;
      let fileName: string | null = null;
      let fileSize: number | null = null;
      let mimeType: string | null = null;

      if (createFile) {
        const ext = createFile.name.split(".").pop() || "bin";
        const storagePath = `${workspaceId}/${crypto.randomUUID()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("workspace-layouts")
          .upload(storagePath, createFile, { contentType: createFile.type });
        if (uploadError) throw uploadError;
        filePath = storagePath;
        fileName = createFile.name;
        fileSize = createFile.size;
        mimeType = createFile.type;
      }

      const { data: user } = await supabase.auth.getUser();

      const { error: insertError } = await supabase.from("workspace_layouts").insert({
        workspace_id: workspaceId,
        type: createType,
        title: createTitle.trim(),
        content: createType === "text" ? createContent.trim() : null,
        file_path: filePath,
        file_name: fileName,
        file_size: fileSize,
        mime_type: mimeType,
        created_by: user?.user?.id || null,
      });

      if (insertError) throw insertError;

      toast({ title: "✅ Layout criado" });
      setCreateDialogOpen(false);
      await fetchLayouts();
    } catch (err: any) {
      toast({ title: "Erro ao criar layout", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (layout: Layout) => {
    setDeleting(layout.id);
    try {
      if (layout.file_path) {
        await supabase.storage.from("workspace-layouts").remove([layout.file_path]);
      }
      const { error } = await supabase.from("workspace_layouts").delete().eq("id", layout.id);
      if (error) throw error;
      setLayouts(prev => prev.filter(l => l.id !== layout.id));
      toast({ title: "Layout removido" });
    } catch (err: any) {
      toast({ title: "Erro ao remover", description: err.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  const handleSendLayout = async (layout: Layout) => {
    if (layout.type === "text" && layout.content) {
      onInsertText(layout.content);
      setOpen(false);
      return;
    }

    if (layout.file_path) {
      try {
        const { data, error } = await supabase.storage
          .from("workspace-layouts")
          .download(layout.file_path);
        if (error) throw error;
        const blob = data as Blob;
        const file = new globalThis.File([blob], layout.file_name || "arquivo", { type: layout.mime_type || "application/octet-stream" });
        onSendFile(file);
        setOpen(false);
      } catch (err: any) {
        toast({ title: "Erro ao baixar arquivo", description: err.message, variant: "destructive" });
      }
    }
  };

  const renderItems = (type: string) => {
    const items = layouts.filter(l => l.type === type);

    if (loading) {
      return (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-4">
          Nenhum layout cadastrado
        </p>
      );
    }

    return (
      <div className="space-y-1.5">
        {items.map(item => (
          <div key={item.id} className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-accent/50 transition-colors">
            <div className="flex-shrink-0">
              {item.type === "text" && <FileText className="w-4 h-4 text-muted-foreground" />}
              {item.type === "image" && <ImageIcon className="w-4 h-4 text-muted-foreground" />}
              {item.type === "file" && <File className="w-4 h-4 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{item.title}</p>
              {item.type === "text" && item.content && (
                <p className="text-xs text-muted-foreground truncate">{item.content}</p>
              )}
              {item.file_name && (
                <p className="text-xs text-muted-foreground truncate">
                  {item.file_name} {item.file_size ? `(${formatFileSize(item.file_size)})` : ""}
                </p>
              )}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => handleSendLayout(item)}
                title={item.type === "text" ? "Inserir no campo" : "Enviar"}
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => handleDelete(item)}
                disabled={deleting === item.id}
                title="Excluir"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /** Conteúdo compartilhado entre popover (botão visível) e dialog (menu ⋮ / ref). */
  const pickerPanel = (
    <Tabs defaultValue="text" className="w-full">
      <div className="flex items-center justify-between px-3 pb-1 pt-3">
        <TabsList className="h-8">
          <TabsTrigger value="text" className="px-2 py-1 text-xs">
            Textos
          </TabsTrigger>
          <TabsTrigger value="image" className="px-2 py-1 text-xs">
            Imagens
          </TabsTrigger>
          <TabsTrigger value="file" className="px-2 py-1 text-xs">
            Documentos
          </TabsTrigger>
        </TabsList>
      </div>

      {(["text", "image", "file"] as const).map(type => (
        <TabsContent key={type} value={type} className="mt-0 px-3 pb-3">
          <div className={showTrigger ? "max-h-56 overflow-y-auto" : "max-h-[min(24rem,55vh)] overflow-y-auto"}>
            {renderItems(type)}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-2 w-full"
            onClick={() => openCreateDialog(type)}
            disabled={countByType(type) >= getMaxForType(type)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Criar {type === "text" ? "texto" : type === "image" ? "imagem" : "documento"} ({countByType(type)}/{getMaxForType(type)})
          </Button>
        </TabsContent>
      ))}
    </Tabs>
  );

  return (
    <>
      {showTrigger ? (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={disabled}
              title="Layouts de mensagens"
              className="h-9 w-9 shrink-0 sm:h-10 sm:w-10"
            >
              <LayoutTemplate className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start" side="top">
            {pickerPanel}
          </PopoverContent>
        </Popover>
      ) : (
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
            <DialogHeader className="shrink-0 border-b px-4 pb-2 pt-4 pr-12 text-left">
              <DialogTitle className="text-base">Modelos e mídia</DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-4 pt-1">{pickerPanel}</div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Novo layout — {createType === "text" ? "Texto" : createType === "image" ? "Imagem" : "Documento"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Título</label>
              <Input
                value={createTitle}
                onChange={e => setCreateTitle(e.target.value)}
                placeholder="Nome do layout"
                maxLength={100}
              />
            </div>

            {createType === "text" ? (
              <div>
                <label className="text-sm font-medium">Conteúdo</label>
                <Textarea
                  value={createContent}
                  onChange={e => setCreateContent(e.target.value)}
                  placeholder="Mensagem padrão..."
                  maxLength={1000}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">{createContent.length}/1000</p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium">Arquivo</label>
                {createFile ? (
                  <div className="flex items-center gap-2 p-2 border rounded-md mt-1">
                    <span className="text-sm truncate flex-1">{createFile.name}</span>
                    <span className="text-xs text-muted-foreground">{formatFileSize(createFile.size)}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setCreateFile(null)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full mt-1"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Selecionar arquivo
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={createType === "image" ? "image/*" : ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) {
                      if (f.size > MAX_FILE_SIZE) {
                        toast({ title: "Arquivo muito grande", description: "Máximo 16MB.", variant: "destructive" });
                        return;
                      }
                      setCreateFile(f);
                    }
                    e.target.value = "";
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">Máximo 16MB</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSubmit} disabled={saving}>
              {saving ? "Salvando..." : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});
LayoutsPopover.displayName = "LayoutsPopover";
